import React, { useId, useState } from 'react';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import { MarkButton, MarkInput, MarkLabel } from '../MarkControls';
import MarkModal from '../MarkModal';
import { useEditorContext } from './EditorContext';
import * as styles from './canvas.module.css';

type Props = {
    fieldId: string;
    onInsert: (value: string) => void;
    disabled?: boolean;
    compact?: boolean;
    mode?: 'text' | 'links';
};
type Selection = {
    element: HTMLInputElement | HTMLTextAreaElement;
    start: number;
    end: number;
    value: string;
};
const PersonalizationButton: React.FC<Props> = ({
    fieldId,
    onInsert,
    disabled,
    compact = false,
    mode = 'text'
}) => {
    const { variables, urlSlugs, canvas, setInsertTarget } = useEditorContext();
    const [selection, setSelection] = useState<Selection | null>(null);
    const [query, setQuery] = useState('');
    const searchId = useId();
    if (!canvas && mode !== 'links') return null;
    const linksOnly = mode === 'links';
    const title = linksOnly ? 'Insert saved link' : 'Insert personalization';
    const entries = [
        ...(linksOnly ? [] : variables).map((variable) => ({
            key: variable.key,
            label: variable.label,
            group: variable.owner
        })),
        ...urlSlugs.map((slug) => ({
            key: slug,
            label: `Saved link: ${slug}`,
            group: 'Saved links'
        }))
    ];
    const matches = entries.filter((entry) =>
        `${entry.label} ${entry.key} ${entry.group}`.toLowerCase().includes(query.toLowerCase())
    );
    const restore = (start: number, end = start) => {
        const element = selection?.element;
        setSelection(null);
        requestAnimationFrame(() => {
            if (!element?.isConnected) return;
            element.focus();
            element.setSelectionRange(start, end);
        });
    };
    return (
        <>
            <MarkButton
                compact={compact}
                disabled={disabled}
                onClick={() => {
                    const element = document.getElementById(fieldId) as
                        HTMLInputElement | HTMLTextAreaElement | null;
                    if (!element) return;
                    setQuery('');
                    setSelection({
                        element,
                        value: element.value,
                        start: element.selectionStart ?? element.value.length,
                        end: element.selectionEnd ?? element.value.length
                    });
                }}
            >
                {title}
            </MarkButton>
            {selection && (
                <MarkModal
                    aria-label={title}
                    onClose={() => restore(selection.start, selection.end)}
                    styles={{ width: 'min(64rem, 94vw)' }}
                >
                    <H role="h4" noDefaultMargin>
                        {title}
                    </H>
                    <P noDefaultMargin>
                        {linksOnly
                            ? 'Choose a saved link to replace this button’s destination.'
                            : 'Choose a recipient detail or saved link to insert into this field.'}
                    </P>
                    <div className={styles.field}>
                        <MarkLabel htmlFor={searchId}>
                            {linksOnly ? 'Search saved links' : 'Search personalization'}
                        </MarkLabel>
                        <MarkInput
                            id={searchId}
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.currentTarget.value)}
                            placeholder="Search by name…"
                        />
                    </div>
                    <div className={styles.personalizationList}>
                        {matches.map((entry) => (
                            <MarkButton
                                className={styles.personalizationChoice}
                                size="regular"
                                key={entry.key}
                                onClick={() => {
                                    const token = `{{${entry.key}}}`;
                                    const value = linksOnly
                                        ? token
                                        : selection.value.slice(0, selection.start) +
                                          token +
                                          selection.value.slice(selection.end);
                                    onInsert(value);
                                    setInsertTarget(null);
                                    restore(
                                        linksOnly ? token.length : selection.start + token.length
                                    );
                                }}
                            >
                                {entry.label}
                            </MarkButton>
                        ))}
                        {matches.length === 0 && (
                            <P noDefaultMargin>
                                {entries.length === 0
                                    ? linksOnly
                                        ? 'No saved links are available. Enter an HTTPS address or mailto link.'
                                        : 'No personalization is available for this audience.'
                                    : 'No matches. Try a different name.'}
                            </P>
                        )}
                    </div>
                    <div className={styles.actions}>
                        <MarkButton
                            size="regular"
                            onClick={() => restore(selection.start, selection.end)}
                        >
                            Cancel
                        </MarkButton>
                    </div>
                </MarkModal>
            )}
        </>
    );
};
export default PersonalizationButton;
